import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { useAuth } from "./AuthContext";

const STORAGE_KEY = "pills-active-member-id";

const FamilyContext = createContext(null);

export const FamilyProvider = ({ children }) => {
    const { user } = useAuth();
    const [members, setMembers] = useState([]);
    const [activeId, setActiveId] = useState(() => {
        if (typeof window === "undefined") return null;
        return window.localStorage.getItem(STORAGE_KEY);
    });
    const [loading, setLoading] = useState(false);

    const reload = useCallback(async () => {
        if (!user) {
            setMembers([]);
            return;
        }
        try {
            setLoading(true);
            const { data } = await api.get("/family/members");
            const items = data?.items || [];
            setMembers(items);
            // Auto-select "self" on first load if no active id is set
            if (!activeId && items.length > 0) {
                const self = items.find((m) => m.is_self) || items[0];
                if (self) setActiveId(self.id);
            } else if (activeId && !items.find((m) => m.id === activeId)) {
                // Active id is no longer valid (member was deleted) — fall back to self
                const self = items.find((m) => m.is_self) || items[0];
                setActiveId(self ? self.id : null);
            }
        } catch {
            setMembers([]);
        } finally {
            setLoading(false);
        }
    }, [user, activeId]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (activeId) window.localStorage.setItem(STORAGE_KEY, activeId);
        else window.localStorage.removeItem(STORAGE_KEY);
    }, [activeId]);

    const activeMember = useMemo(
        () => members.find((m) => m.id === activeId) || members.find((m) => m.is_self) || null,
        [members, activeId]
    );

    // If active member is "self" → backend treats family_member_id=null as self.
    // For health endpoints we still send the id (so caregivers can switch).
    const activeFamilyMemberQuery = useMemo(() => {
        if (!activeMember) return {};
        return { family_member_id: activeMember.id };
    }, [activeMember]);

    const value = useMemo(
        () => ({
            members,
            activeMember,
            activeId: activeMember?.id || null,
            setActiveId,
            reload,
            loading,
            activeFamilyMemberQuery,
        }),
        [members, activeMember, loading, activeFamilyMemberQuery, reload]
    );

    return <FamilyContext.Provider value={value}>{children}</FamilyContext.Provider>;
};

export const useFamily = () => {
    const ctx = useContext(FamilyContext);
    if (!ctx) return { members: [], activeMember: null, activeId: null, setActiveId: () => {}, reload: () => {}, loading: false, activeFamilyMemberQuery: {} };
    return ctx;
};
